import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * @extends {Component<{form: HTMLFormElement, email: HTMLInputElement,
 * submitButton: HTMLButtonElement, status: HTMLElement, disclosure: HTMLDetailsElement,
 * variantTitle: HTMLElement}>}
 */
class KlaviyoBackInStock extends Component {
  requiredRefs = ['form', 'email', 'submitButton', 'status', 'disclosure', 'variantTitle'];
  #listeners;
  #request;
  #selection = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#selectVariant, {
      signal: this.#listeners.signal,
    });
    this.#updateVisibility();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#reset();
  }

  #reset() {
    this.#selection++;
    this.#request?.abort();
    this.#request = undefined;
    this.refs.submitButton.disabled = false;
    this.refs.form.removeAttribute('aria-busy');
    this.refs.status.textContent = '';
  }

  #updateVisibility() {
    this.hidden = !this.dataset.variantId || this.dataset.available !== 'false';
  }

  #selectVariant = async (event) => {
    // Ignore recommendation cards and Quick Add selections within the same section.
    if (event.target.closest('product-card, dialog')) return;
    const selectedProductId = String(event.product?.id ?? '').split('/').pop();
    if (selectedProductId && selectedProductId !== this.dataset.productId) return;
    this.#reset();
    this.hidden = true;
    this.refs.disclosure.open = false;
    const selection = this.#selection;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || selection !== this.#selection) return;
      const variant = detail?.resource;
      if (detail?.newProduct) this.dataset.productId = String(detail.newProduct.id);
      this.dataset.variantId = variant?.id == null ? '' : String(variant.id);
      this.dataset.available = String(variant?.available ?? true);
      this.refs.variantTitle.textContent = variant?.title ?? '';
      this.#updateVisibility();
    } catch {
      // Keep hidden when a selection could not resolve; never subscribe a stale variant.
    }
  };

  /** @param {SubmitEvent} event */
  async subscribe(event) {
    event.preventDefault();
    if (this.hidden || this.#request || !this.refs.form.reportValidity()) return;
    const variantId = this.dataset.variantId;
    if (!variantId || this.dataset.available !== 'false') return;
    const request = this.#request = new AbortController();
    this.refs.submitButton.disabled = true;
    this.refs.form.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = '';
    const timeout = setTimeout(() => request.abort(), 15000);

    try {
      const url = new URL('https://a.klaviyo.com/client/back-in-stock-subscriptions/');
      url.searchParams.set('company_id', this.dataset.companyId);
      const response = await fetch(url, {
        method: 'POST',
        signal: request.signal,
        headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: this.refs.email.value.trim() } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
            },
          },
        }),
      });
      if (response.status !== 202) throw new Error('Restock subscription not accepted');
      if (this.#request !== request || !this.isConnected) return;
      this.refs.status.textContent = this.dataset.success;
      this.refs.form.reset();
    } catch {
      if (this.#request === request && this.isConnected) this.refs.status.textContent = this.dataset.error;
    } finally {
      clearTimeout(timeout);
      if (this.#request === request) {
        this.#request = undefined;
        this.refs.submitButton.disabled = false;
        this.refs.form.removeAttribute('aria-busy');
      }
    }
  }
}

if (!customElements.get('klaviyo-back-in-stock')) {
  customElements.define('klaviyo-back-in-stock', KlaviyoBackInStock);
}
